import axios from 'axios';
import logger from './logger';
import { safeReturnPath } from './authNavigation';
import { clearJBrowseAssetCache } from './jbrowseAssetCache';

const SAME_ORIGIN_API_URL = '__SAME_ORIGIN__';

const currentBrowserOrigin = () => {
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }
  return '';
};

const resolveApiUrl = () => {
  const configuredUrl = process.env.REACT_APP_API_URL;
  if (configuredUrl === SAME_ORIGIN_API_URL) {
    return currentBrowserOrigin();
  }
  if (configuredUrl) {
    return configuredUrl;
  }
  if (process.env.NODE_ENV === 'production') {
    return currentBrowserOrigin();
  }
  return 'http://localhost:8000';
};

// Create a configured axios instance with the API URL from environment variables
const API_URL = resolveApiUrl();

const api = axios.create({
  baseURL: API_URL,
});

const isAccountRequest = url =>
  /\/api\/(?:token|register|user|password-reset|verify-email)(?:\/|$)/.test(url || '');
const logRequestUrl = url => (url || '').replace(/(\/verify-email\/)[^/?]+/g, '$1[redacted]');

const isFormDataPayload = value => typeof FormData !== 'undefined' && value instanceof FormData;

const removeContentTypeHeader = headers => {
  if (!headers) return;

  if (typeof headers.delete === 'function') {
    headers.delete('Content-Type');
    headers.delete('content-type');
    return;
  }

  delete headers['Content-Type'];
  delete headers['content-type'];

  if (headers.common) {
    delete headers.common['Content-Type'];
    delete headers.common['content-type'];
  }
};

// Add request interceptor to include auth token with every request
api.interceptors.request.use(
  config => {
    config.headers = config.headers || {};

    if (isFormDataPayload(config.data)) {
      removeContentTypeHeader(config.headers);
    }

    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Token ${token}`;
    }

    // Add request ID for tracking
    config.headers['X-Request-ID'] = `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    // Store request start time
    config.metadata = { startTime: new Date() };

    logger.debug('API Request', {
      method: config.method.toUpperCase(),
      url: logRequestUrl(config.url),
      requestId: config.headers['X-Request-ID'],
    });

    return config;
  },
  error => {
    logger.error('API Request Error', {
      message: error.message,
      stack: error.stack,
    });
    return Promise.reject(error);
  }
);

// Add a response interceptor to handle common errors
api.interceptors.response.use(
  response => {
    const duration = response.config.metadata?.startTime
      ? new Date() - response.config.metadata.startTime
      : 0;

    logger.api(
      response.config.method.toUpperCase(),
      logRequestUrl(response.config.url),
      response.status,
      duration,
      {
        requestId: response.config.headers['X-Request-ID'],
        responseSize: JSON.stringify(response.data)?.length || 0,
      }
    );

    return response;
  },
  error => {
    const duration =
      error.config && error.config.metadata ? new Date() - error.config.metadata.startTime : 0;

    const status = error.response ? error.response.status : 0;
    const errorData = {
      requestId: error.config?.headers?.['X-Request-ID'],
      message: error.message,
      responseData: error.response?.data,
      stack: error.stack,
    };

    logger.api(
      error.config?.method?.toUpperCase() || 'UNKNOWN',
      logRequestUrl(error.config?.url) || 'unknown',
      status,
      duration,
      errorData
    );

    // Handle authentication errors
    if (error.response?.status === 401 && !isAccountRequest(error.config?.url)) {
      logger.warn('Authentication failed, redirecting to login');
      // Clear token and redirect to login
      clearJBrowseAssetCache();
      localStorage.removeItem('auth_token');
      localStorage.removeItem('username');
      const returnTo = safeReturnPath(
        `${window.location.pathname}${window.location.search}${window.location.hash}`
      );
      window.location.href = `/login?returnTo=${encodeURIComponent(returnTo)}`;
    }

    return Promise.reject(error);
  }
);

// Consolidated API services
const apiService = {
  // Base axios instance - can be used for custom requests
  client: api,

  // Authentication
  auth: {
    login: (username, password) => api.post('/api/token/', { username, password }),
    register: data => api.post('/api/register/', data),
  },

  // Datasets
  datasets: {
    getAll: (params = {}) =>
      api.get('/api/datasets/', { params: { exclude_smoke: true, ...params } }),
    getOne: (id, options = {}) => api.get(`/api/datasets/${id}/`, options),
    create: data => api.post('/api/datasets/', data),
    update: (id, data) => api.put(`/api/datasets/${id}/`, data),
    patch: (id, data) => api.patch(`/api/datasets/${id}/`, data),
    assignCategory: data => api.post('/api/datasets/assign-category/', data),
    delete: id => api.delete(`/api/datasets/${id}/`),
    buildAnnotationSearchIndex: (id, data = {}) =>
      api.post(`/api/datasets/${id}/build-annotation-search-index/`, data),
    getAnnotationSearchAttributes: (id, options = {}) =>
      api.get(`/api/datasets/${id}/annotation-search-attributes/`, options),
    listCollaborators: id => api.get(`/api/datasets/${id}/collaborators/`),
    addCollaborator: (id, data) => api.post(`/api/datasets/${id}/collaborators/add/`, data),
    removeCollaborator: (id, userId) => api.delete(`/api/datasets/${id}/collaborators/${userId}/`),
    getUploadUrl: data => api.post('/api/datasets/generate_upload_url/', data),
    confirmUpload: data => api.post('/api/datasets/confirm_upload/', data),
    getDownloadUrl: id => api.post(`/api/datasets/${id}/generate_download_url/`),
    getPresignedUrl: id => api.post(`/api/datasets/${id}/generate_download_url/`),
    batchDownload: datasetIds =>
      api.post(
        '/api/datasets/batch-download/',
        { dataset_ids: datasetIds },
        { responseType: 'blob' }
      ),
    getTrackConfig: (id, assembly, options = {}) =>
      api.get(`/api/datasets/${id}/track_config/`, { ...options, params: { assembly } }),
    getTrackCatalog: (assembly, params = {}, options = {}) =>
      api.get('/api/datasets/track-catalog/', { ...options, params: { assembly, ...params } }),
    getReferenceTracks: (assembly, params = {}, options = {}) =>
      api.get('/api/datasets/reference_tracks/', { ...options, params: { assembly, ...params } }),
  },

  // BLAST
  blast: {
    submit: data => api.post('/blast/api/blast/', data),
    getJobStatus: (jobId, options = {}) => api.get(`/blast/api/blast-jobs/${jobId}/`, options),
    getAll: params => api.get('/api/blast-results/', params ? { params } : undefined),
    getOne: id => api.get(`/api/blast-results/${id}/`),
    index: datasetId => api.post('/blast/index-dataset/', { dataset_id: datasetId }),
    indexDataFile: dataFileId => api.post('/blast/index-dataset/', { data_file_id: dataFileId }),
    indexAssembly: assemblyId => api.post('/blast/index-assembly/', { assembly_id: assemblyId }),
    listIndexingJobs: params => api.get('/blast/indexing-jobs/', params ? { params } : undefined),
    getIndexingJob: (jobId, options = {}) => api.get(`/blast/indexing-jobs/${jobId}/`, options),
    visualize: blastResultId => api.get(`/blast/visualize-blast/${blastResultId}/`),
    getBlastDatabases: () => api.get('/blast/api/blast-databases/'),
    getNcbiDatabases: () => api.get('/blast/api/ncbi-databases/'),
  },

  // JBrowse
  jbrowse: {
    getConfig: datasetId => api.get(`/blast/jbrowse-config/${datasetId}/`),
  },

  // DataFiles
  datafiles: {
    list: params => api.get('/api/datafiles/', { params }),
    getOne: id => api.get(`/api/datafiles/${id}/`),
    update: (id, data) => api.patch(`/api/datafiles/${id}/`, data),
    delete: id => api.delete(`/api/datafiles/${id}/`),
    getUploadUrl: data => api.post('/api/datafiles/generate_upload_url/', data),
    confirmUpload: data => api.post('/api/datafiles/confirm_upload/', data),
    uploadLocal: (uploadUrl, file) => {
      const formData = new FormData();
      formData.append('file', file);
      return api.post(uploadUrl, formData);
    },
    uploadToS3: (uploadUrl, fields, file) => {
      const formData = new FormData();
      Object.entries(fields || {}).forEach(([key, value]) => formData.append(key, value));
      formData.append('file', file);
      return api.post(uploadUrl, formData);
    },
  },

  // Track Hubs & Tracks
  trackhubs: {
    list: () => api.get('/api/trackhubs/'),
    create: data => api.post('/api/trackhubs/', data),
    config: id => api.get(`/api/trackhubs/${id}/config/`),
    update: (id, data) => api.patch(`/api/trackhubs/${id}/`, data),
    delete: id => api.delete(`/api/trackhubs/${id}/`),
  },

  tracks: {
    create: data => api.post('/api/tracks/', data),
    update: (id, data) => api.patch(`/api/tracks/${id}/`, data),
  },

  // Browser Tracks (persisted user track selections)
  browserTracks: {
    list: (params = {}, options = {}) => api.get('/api/browser-tracks/', { ...options, params }),
    create: data => api.post('/api/browser-tracks/', data),
    delete: id => api.delete(`/api/browser-tracks/${id}/`),
    update: (id, data) => api.patch(`/api/browser-tracks/${id}/`, data),
    updateOrder: tracks => api.post('/api/browser-tracks/bulk_update_order/', { tracks }),
  },

  // Genome Assemblies
  assemblies: {
    downloads: () => api.get('/api/assemblies/downloads/', { params: { exclude_smoke: true } }),
    list: () => api.get('/api/assemblies/', { params: { exclude_smoke: true } }),
    getOne: id => api.get(`/api/assemblies/${id}/`),
    create: data => api.post('/api/assemblies/', data),
    update: (id, data) => api.put(`/api/assemblies/${id}/`, data),
    delete: id => api.delete(`/api/assemblies/${id}/`),
  },

  // Synteny Maps
  synteny: {
    list: () => api.get('/api/synteny-maps/'),
    generateUploadUrl: data => api.post('/api/synteny-maps/generate_upload_url/', data),
    confirmUpload: data => api.post('/api/synteny-maps/confirm_upload/', data),
    registerDataset: data => api.post('/api/synteny-maps/register-dataset/', data),
    getConfig: id => api.get(`/api/synteny-maps/${id}/jbrowse-config/`),
  },

  // Storage (public buckets)
  storage: {
    listPublicObjects: () => api.get('/api/storage/public-objects/'),
    registerPublicObjectDataset: data =>
      api.post('/api/storage/public-objects/register-dataset/', data),
    registerPublicObjectAssembly: data =>
      api.post('/api/storage/public-objects/register-assembly/', data),
  },

  platform: {
    getVersion: () => api.get('/api/version/'),
  },

  platformMemberships: {
    deleteAccount: userId => api.delete(`/api/platform-memberships/${userId}/delete-account/`),
    list: () => api.get('/api/platform-memberships/'),
    update: (userId, role) => api.patch(`/api/platform-memberships/${userId}/`, { role }),
    invite: data => api.post('/api/platform-memberships/invite/', data),
  },

  uploadIntents: {
    resolve: data => api.post('/api/upload-intents/resolve/', data),
  },

  // Convenience methods for backward compatibility
  getDataset: id => api.get(`/api/datasets/${id}/`),
  getBlastResult: id => api.get(`/api/blast-results/${id}/`),
  getJBrowseConfig: datasetId => api.get(`/blast/jbrowse-config/${datasetId}/`),
  visualizeBlastResult: (blastResultId, params) =>
    api.get(`/blast/visualize-blast/${blastResultId}/`, { params }),
};

// Backwards compatibility helpers used across the frontend
apiService.getDatasets = () => apiService.datasets.getAll();
apiService.getBlastResults = () => apiService.blast.getAll();
apiService.submitBlastQuery = data => apiService.blast.submit(data);
apiService.listPublicObjects = () => apiService.storage.listPublicObjects();

export default apiService;
